import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, firebaseConfigValid } from '@/lib/firebase';

const AuthContext = createContext(null);

// La creazione del profilo /users/{uid} e' server-side (Cloud Function
// onUserCreated, trigger Firebase Auth). Il client legge soltanto e attende
// la CF in polling. Senza polling il primo signup vedrebbe "unauthorized"
// per la frazione di secondo in cui la CF sta scrivendo.
const PROFILE_POLL_MAX_ATTEMPTS = 15;
const PROFILE_POLL_INTERVAL_MS = 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUserProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser || !db) {
      setUserProfile(null);
      return null;
    }
    const userRef = doc(db, 'users', firebaseUser.uid);

    for (let attempt = 0; attempt < PROFILE_POLL_MAX_ATTEMPTS; attempt++) {
      try {
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const profile = snap.data();
          if (profile.disabled) {
            setUserProfile({ disabled: true });
            return { disabled: true };
          }
          if (profile.unauthorized) {
            setUserProfile({ unauthorized: true });
            return { unauthorized: true };
          }
          setUserProfile(profile);
          return profile;
        }
      } catch (e) {
        console.warn(`Tentativo ${attempt + 1}/${PROFILE_POLL_MAX_ATTEMPTS} lettura profilo fallito:`, e);
      }
      await new Promise((r) => setTimeout(r, PROFILE_POLL_INTERVAL_MS));
    }

    // Timeout: la Cloud Function onUserCreated non ha scritto il profilo entro
    // ~15s. Probabile failure (quota Firestore, bug CF, errore SMTP del trigger
    // onInviteCreated che impedisce la catena, ecc.). Log di ricerca in Cloud
    // Logging sul progetto los-tracking, filtro "onUserCreated".
    console.error('Profilo utente non disponibile dopo il timeout CF; fallback a unauthorized');
    setUserProfile({ unauthorized: true });
    return { unauthorized: true };
  }, []);

  // Listener real-time sul documento utente: rileva revoca accesso immediata
  // (disabled=true) o modifiche di ruolo/studio decise server-side.
  useEffect(() => {
    if (!firebaseConfigValid || !db || !user) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.disabled) {
        setUserProfile({ disabled: true });
      } else if (data.unauthorized) {
        setUserProfile({ unauthorized: true });
      } else {
        setUserProfile(data);
      }
    });
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!firebaseConfigValid || !auth) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        await loadUserProfile(u);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [loadUserProfile]);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signup = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const loginWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const signOut = () => {
    setUserProfile(null);
    return fbSignOut(auth);
  };

  const resetPassword = (email) =>
    sendPasswordResetEmail(auth, email);

  const isPasswordProvider = () =>
    !!auth?.currentUser?.providerData?.some((p) => p.providerId === 'password');

  const changePassword = async (currentPassword, newPassword) => {
    const currentUser = auth?.currentUser;
    if (!currentUser || !currentUser.email) throw new Error('Utente non autenticato');
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
  };

  const role = userProfile?.role || null;
  const unauthorized = userProfile?.unauthorized === true;
  const disabled = userProfile?.disabled === true;

  return (
    <AuthContext.Provider value={{
      user, loading, login, signup, loginWithGoogle, signOut,
      resetPassword, changePassword, isPasswordProvider,
      userProfile, role, unauthorized, disabled, refreshProfile: () => user && loadUserProfile(user),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
